if (typeof debounce === 'undefined') {
  var debounce = function(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.mobileMenuDrawer = this.querySelector('.mobile-menu-drawer');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');
      this._scrollTicking = false;
      this._stickyOffset = 0;
      this._closeAnimations = new WeakMap();
      this._menuAnimationDuration = 250;

      document.addEventListener('keyup', (e) => {
        if (e.code?.toUpperCase() === 'ESCAPE') {
          this.toggle.removeAttribute('open');
          this.toggle.classList.remove('active');
        }
      });
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.setLayer2Fullscreen(false);
          this.closeAnimation(this.toggle);
          // The mobile-menu-open header override just got removed, so
          // --header-offset must be recalculated now rather than waiting on scroll.
          this.setHeaderMetrics();
          // Reset only the fullscreen (layer-2) submenus now, not just the
          // drawer-level flag, so reopening never resumes a fullscreen view.
          // The regular accordion levels (e.g. MEN) are left as they were.
          this.menu.querySelectorAll('details:has(> summary.layer-2)').forEach(details => {
            this.cancelCloseAnimation(details);
            details.removeAttribute('open');
            details.classList.remove('menu-opening', 'layer-2-fullscreen-open', 'layer-2-fullscreen-closing', 'submenu-open');
          });
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
            // The mobile-menu-open header override just applied, so
            // --header-offset must be recalculated now rather than waiting on scroll.
            this.setHeaderMetrics();
          });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });

      // Cache sticky offset and recalculate on resize
      this._cacheStickyOffset();
      window.addEventListener('resize', debounce(() => {
        this._cacheStickyOffset();
      }, 100));

      // Single scroll listener with rAF throttle
      window.addEventListener('scroll', () => {
        if (!this._scrollTicking) {
          this._scrollTicking = true;
          requestAnimationFrame(() => {
            this.setStickyClass();
            this.setHeaderOffset();
            this.setHeaderHeight();
            this._scrollTicking = false;
          });
        }
      }, { passive: true });

      window.dispatchEvent(new Event('scroll'));
      this.setHeaderMetrics();

      if (document.querySelector('.announcement-bar-section')) {
        this.setAnnouncementHeight();
        window.addEventListener('scroll', () => this.setAnnouncementHeight(), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }

    _cacheStickyOffset() {
      if (this.classList.contains('header-sticky--active')) {
        // Skip recalculation while sticky — the header is position:sticky at top:0,
        // so getBoundingClientRect().top would return 0 and corrupt the offset.
        if (this.classList.contains('is-sticky')) {
          return;
        }
        this._stickyOffset = parseInt(this.header_section.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
      }
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        this.classList.toggle('is-sticky', window.scrollY >= this._stickyOffset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      if (a_bar) {
        let h = a_bar.clientHeight;
        document.documentElement.style.setProperty('--announcement-height', h + 'px');
      }
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    setHeaderMetrics() {
      this.setHeaderOffset();
      this.setHeaderHeight();
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');

      this.cancelCloseAnimation(detailsElement);
      detailsElement.classList.remove('layer-2-fullscreen-closing');

      // Stamp deferred template content on first open
      const tpl = detailsElement.querySelector(':scope > template.mobile-menu-deferred');
      if (tpl) {
        tpl.replaceWith(tpl.content.cloneNode(true));
        // Re-bind back buttons for the newly stamped content
        detailsElement.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
        detailsElement.querySelectorAll('details.link-container summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      }

      if (summaryElement.classList.contains('layer-2')) {
        this.setHeaderMetrics();
        this.setLayer2Fullscreen(true);
        detailsElement.classList.add('layer-2-fullscreen-open');
        this.resetSubmenuScroll(detailsElement);
      }

      // Wait one paint so the browser commits the closed state first, otherwise
      // the transition to the open state gets skipped instead of animating.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }));
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      const isLayer2Fullscreen = detailsElement.classList.contains('layer-2-fullscreen-open');

      if (isLayer2Fullscreen) {
        detailsElement.classList.add('layer-2-fullscreen-closing');
      }

      this.closeAnimation(detailsElement, () => {
        if (isLayer2Fullscreen) {
          detailsElement.classList.remove('layer-2-fullscreen-open', 'layer-2-fullscreen-closing');
          this.setLayer2Fullscreen(false);
        }
      });
    }
    setLayer2Fullscreen(isActive) {
      this.menu.classList.toggle('layer-2-fullscreen-active', isActive);
      if (this.mobileMenuDrawer) {
        this.mobileMenuDrawer.classList.toggle('layer-2-fullscreen-active', isActive);
        if (isActive) {
          // The fullscreen submenu's fixed position is relative to this drawer
          // (its own transform makes it a containing block). If the drawer's
          // own entrance transform hasn't finished settling yet (fast tapping
          // through hamburger -> submenu), that residual offset would leak into
          // the submenu's slide-in as an unwanted diagonal motion. Snap it to its
          // resting transform instantly, now that the drawer is already in its
          // fullscreen-active layout, before the submenu starts animating.
          this.mobileMenuDrawer.style.transition = 'none';
          this.mobileMenuDrawer.getBoundingClientRect();
          this.mobileMenuDrawer.style.transition = '';
        }
      }
    }
    resetSubmenuScroll(detailsElement) {
      const submenu = detailsElement.querySelector(':scope > .sub-menu');
      if (submenu) {
        submenu.scrollTop = 0;
      }
    }
    closeAnimation(detailsElement, onComplete) {
      this.cancelCloseAnimation(detailsElement);

      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < this._menuAnimationDuration) {
          this._closeAnimations.set(detailsElement, window.requestAnimationFrame(handleAnimation));
        } else {
          this._closeAnimations.delete(detailsElement);
          detailsElement.removeAttribute('open');
          onComplete?.();
        }
      };

      this._closeAnimations.set(detailsElement, window.requestAnimationFrame(handleAnimation));
    }
    cancelCloseAnimation(detailsElement) {
      const animationFrame = this._closeAnimations.get(detailsElement);

      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        this._closeAnimations.delete(detailsElement);
      }
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      // resize on initial load
      window.addEventListener('resize', debounce(() => {
        this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(() => {
        this.resizeSubMenus();
      });
    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
